// Tool definitions cho widget broker (Approach B). 3 nhóm tool:
//
//   1. Read tools (list/get/preview/find): call API direct, return data
//   2. Mutation tools (create/update/delete/set/cancel/void/book): qua executeViaForm
//      → navigate route + prefill form. User click Submit = approve, API call qua
//      form's normal path, result feed back AI.
//   3. Typed navigation tools (view_*): sinh tự động từ BOOKING_ROUTES registry.
//      Schema bắt buộc real ID, AI không hallucinate path.

import type { Tool } from "./agent-sdk";
import { BOOKING_ROUTES } from "./agent-routes";
import type { ExecuteViaFormOptions } from "./solid-agent-ui";
import { createTypedNavTools } from "./solid-agent-ui";
import { api } from "./api";

/// Inject navigate + executeViaForm từ component khi build tools.
export function buildBookingTools(
  navigate: (path: string) => void,
  executeViaForm: (opts: ExecuteViaFormOptions) => Promise<unknown>,
): Tool[] {
  return [
    // ── Booking items ─────────────────────────────────────────────
    {
      name: "list_booking_items",
      description: "List paginated booking items",
      parameters: {
        type: "object",
        properties: {
          limit: { type: "number" },
          offset: { type: "number" },
        },
      },
      execute: async ({ limit, offset }: { limit?: number; offset?: number }) =>
        api.bookingItems.list(limit ?? 50, offset ?? 0),
      risk: "low",
    },
    {
      name: "get_booking_item",
      description: "Get a booking item by id",
      parameters: {
        type: "object",
        properties: { id: { type: "string" } },
        required: ["id"],
      },
      execute: async ({ id }: { id: string }) => api.bookingItems.get(id),
      risk: "low",
    },
    {
      name: "preview_booking_item",
      description: "Preview a booking item with user quota",
      parameters: {
        type: "object",
        properties: { id: { type: "string" } },
        required: ["id"],
      },
      execute: async ({ id }: { id: string }) => api.bookingItems.preview(id),
      risk: "low",
    },
    {
      name: "create_booking_item",
      description:
        "ADMIN: Create a NEW booking item template (a bookable resource). Use this when user says 'tạo item/ticket/slot mới'. " +
        "REQUIRED: name + total_slots PHẢI có giá trị thực từ USER (qua prompt hoặc ask_user). " +
        "KHÔNG tự đặt tên (vd 'default', 'new item', 'untitled') — luôn ask_user nếu user không cung cấp tên. " +
        "DO NOT call find_item_by_name first when CREATING (item doesn't exist yet). " +
        "DIFFERENT from book_item (book_item = user reserve EXISTING item, not creating).",
      parameters: {
        type: "object",
        properties: {
          name: { type: "string" },
          description: { type: "string" },
          total_slots: { type: "number" },
          group_id: { type: "string" },
        },
        required: ["name", "total_slots"],
      },
      execute: async (args: { name: string; description?: string; total_slots: number; group_id?: string }) =>
        executeViaForm({ toolName: "create_booking_item", route: "/admin/items", intent: "create", args }),
      risk: "medium",
      // No needsApproval — form's Submit button = approval
    },
    {
      name: "update_booking_item",
      description: "Update a booking item (admin only). User reviews changes + submits via form.",
      parameters: {
        type: "object",
        properties: {
          id: { type: "string" },
          name: { type: "string" },
          description: { type: "string" },
          total_slots: { type: "number" },
          group_id: { type: "string" },
        },
        required: ["id"],
      },
      execute: async (args: { id: string; name?: string; description?: string; total_slots?: number; group_id?: string }) =>
        executeViaForm({ toolName: "update_booking_item", route: "/admin/items", intent: "edit", args }),
      risk: "medium",
    },
    {
      name: "delete_booking_item",
      description: "Soft-delete a booking item. High-risk. User confirms via dialog.",
      parameters: {
        type: "object",
        properties: { id: { type: "string" } },
        required: ["id"],
      },
      execute: async (args: { id: string }) =>
        executeViaForm({ toolName: "delete_booking_item", route: "/admin/items", intent: "delete", args }),
      risk: "high",
    },

    // ── Groups ────────────────────────────────────────────────────
    {
      name: "list_groups",
      description: "List groups",
      parameters: {
        type: "object",
        properties: { limit: { type: "number" }, offset: { type: "number" } },
      },
      execute: async ({ limit, offset }: { limit?: number; offset?: number }) =>
        api.groups.list(limit ?? 100, offset ?? 0),
      risk: "low",
    },
    {
      name: "get_group",
      description: "Get a group by id",
      parameters: {
        type: "object",
        properties: { id: { type: "string" } },
        required: ["id"],
      },
      execute: async ({ id }: { id: string }) => api.groups.get(id),
      risk: "low",
    },
    {
      name: "create_group",
      description:
        "Create a new group. User reviews + submits via form. " +
        "REQUIRED: name PHẢI có giá trị thực từ USER (qua prompt hoặc ask_user). " +
        "KHÔNG tự đặt tên (vd 'default group', 'new group', 'untitled') — luôn ask_user nếu user không cung cấp tên.",
      parameters: {
        type: "object",
        properties: { name: { type: "string" }, description: { type: "string" } },
        required: ["name"],
      },
      execute: async (args: { name: string; description?: string }) =>
        executeViaForm({ toolName: "create_group", route: "/admin/groups", intent: "create", args }),
      risk: "medium",
    },
    {
      name: "update_group",
      description: "Update a group's name or description. User reviews + submits.",
      parameters: {
        type: "object",
        properties: { id: { type: "string" }, name: { type: "string" }, description: { type: "string" } },
        required: ["id"],
      },
      execute: async (args: { id: string; name?: string; description?: string }) =>
        executeViaForm({ toolName: "update_group", route: "/admin/groups/:id", intent: "edit", args }),
      risk: "medium",
    },
    {
      name: "delete_group",
      description: "Soft-delete a group. High-risk. User confirms via dialog.",
      parameters: {
        type: "object",
        properties: { id: { type: "string" } },
        required: ["id"],
      },
      execute: async (args: { id: string }) =>
        executeViaForm({ toolName: "delete_group", route: "/admin/groups", intent: "delete", args }),
      risk: "high",
    },

    // ── Flow config ───────────────────────────────────────────────
    {
      name: "get_group_flow",
      description: "Get 2-step flow config of a group",
      parameters: {
        type: "object",
        properties: { id: { type: "string" } },
        required: ["id"],
      },
      execute: async ({ id }: { id: string }) => api.groups.getFlow(id),
      risk: "low",
    },
    {
      name: "set_group_flow",
      description: "Set 2-step flow config (confirm or chain mode). User reviews + submits via group detail page.",
      parameters: {
        type: "object",
        properties: {
          id: { type: "string" },
          enabled: { type: "boolean" },
          mode: { type: "string", enum: ["confirm", "chain"] },
          timeout_seconds: { type: "number" },
          target_group_id: { type: "string" },
        },
        required: ["id", "enabled", "mode", "timeout_seconds"],
      },
      execute: async (args: { id: string; enabled: boolean; mode: "confirm" | "chain"; timeout_seconds: number; target_group_id?: string | null }) =>
        executeViaForm({ toolName: "set_group_flow", route: "/admin/groups/:id", intent: "setFlow", args }),
      risk: "medium",
    },

    // ── Tickets ──────────────────────────────────────────────────
    {
      name: "list_my_tickets",
      description: "List the current user's tickets",
      parameters: {
        type: "object",
        properties: { limit: { type: "number" }, offset: { type: "number" } },
      },
      execute: async ({ limit, offset }: { limit?: number; offset?: number }) =>
        api.tickets.mine(limit ?? 50, offset ?? 0),
      risk: "low",
    },
    {
      name: "cancel_ticket",
      description: "Cancel a ticket owned by current user. User confirms via dialog.",
      parameters: {
        type: "object",
        properties: { ticket_id: { type: "string" } },
        required: ["ticket_id"],
      },
      execute: async (args: { ticket_id: string }) =>
        executeViaForm({ toolName: "cancel_ticket", route: "/me/tickets", intent: "cancel", args }),
      risk: "medium",
    },
    {
      name: "void_ticket",
      description: "Admin: void a ticket with reason. High-risk. User confirms via dialog.",
      parameters: {
        type: "object",
        properties: {
          ticket_id: { type: "string" },
          reason: { type: "string" },
        },
        required: ["ticket_id", "reason"],
      },
      execute: async (args: { ticket_id: string; reason: string }) =>
        executeViaForm({ toolName: "void_ticket", route: "/admin/tickets", intent: "void", args }),
      risk: "high",
    },

    // ── Lookup helpers (resolve name → ID) ───────────────────────
    {
      name: "find_item_by_name",
      description:
        "Search EXISTING booking items by name. Returns matches with id + name + slots. " +
        "ONLY use when user references an EXISTING item by name to get its real ID (vd before book_item, update_booking_item, delete_booking_item, view_item_detail). " +
        "DO NOT use when CREATING new items (create_booking_item) — item doesn't exist yet.",
      parameters: {
        type: "object",
        properties: {
          name: { type: "string", description: "Name fragment to search" },
        },
        required: ["name"],
      },
      execute: async ({ name }: { name: string }) => {
        const all = await api.bookingItems.list(200, 0);
        const needle = name.toLowerCase();
        return all
          .filter((i) => i.name.toLowerCase().includes(needle))
          .map((i) => ({
            id: i.id,
            name: i.name,
            total_slots: i.total_slots,
            availability: i.availability,
            group_id: i.group_id,
          }));
      },
      risk: "low",
    },
    {
      name: "find_group_by_name",
      description:
        "Search EXISTING groups by name. Returns matches with id + name. " +
        "ONLY use when user references an EXISTING group by name to get its real ID (vd before update_group, delete_group, set_group_flow, view_admin_group_detail). " +
        "DO NOT use when CREATING new groups (create_group) — group doesn't exist yet.",
      parameters: {
        type: "object",
        properties: {
          name: { type: "string", description: "Name fragment to search" },
        },
        required: ["name"],
      },
      execute: async ({ name }: { name: string }) => {
        const all = await api.groups.list(200, 0);
        const needle = name.toLowerCase();
        return all
          .filter((g) => g.name.toLowerCase().includes(needle))
          .map((g) => ({ id: g.id, name: g.name, description: g.description }));
      },
      risk: "low",
    },

    // ── User booking action ──────────────────────────────────────
    {
      name: "book_item",
      description:
        "USER ACTION: Reserve a slot for current user on an EXISTING booking item. " +
        "ONLY use when user wants to BOOK/RESERVE an item that ALREADY EXISTS in system. " +
        "DO NOT use for creating new items — that's create_booking_item. " +
        "Requires REAL item ID (UUID) — call find_item_by_name first if user gave name only.",
      parameters: {
        type: "object",
        properties: {
          item_id: { type: "string", description: "Real item UUID" },
        },
        required: ["item_id"],
      },
      execute: async (args: { item_id: string }) =>
        executeViaForm({
          toolName: "book_item",
          route: "/items/:id",
          intent: "book",
          args: { id: args.item_id },
        }),
      risk: "medium",
    },

    // ── Typed navigation (sinh từ BOOKING_ROUTES registry) ──────
    // Mỗi route → 1 tool view_*. AI không hallucinate path vì schema bắt buộc
    // real ID cho dynamic routes.
    ...createTypedNavTools(BOOKING_ROUTES, navigate).map((nav) => ({
      ...nav,
      execute: nav.execute as Tool["execute"],
      risk: "low" as const,
    })),
  ];
}
